import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Link as LinkIcon, Check, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useContactInvitationStatus } from "@/hooks/use-contact-invitation-status";
import { generateInvitationLink } from "@/utils/generateInvitationLink";
import { useQueryClient } from "@tanstack/react-query";
import type { Contact } from "@/hooks/use-contacts";

interface CopyInvitationLinkButtonProps {
  contact: Contact;
}

export const CopyInvitationLinkButton = ({ contact }: CopyInvitationLinkButtonProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: invitationStatus } = useContactInvitationStatus(
    contact.email,
    contact.organization_id
  );
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [manualLink, setManualLink] = useState<string | null>(null);

  // Tries the modern clipboard API, then a legacy fallback for browsers
  // that block it (insecure context, unfocused window, embedded frames).
  const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (clipboardError) {
      console.warn("Clipboard API blocked, trying fallback:", clipboardError);
    }

    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);
      return ok;
    } catch (fallbackError) {
      console.warn("Legacy clipboard fallback failed:", fallbackError);
      return false;
    }
  };

  const handleCopy = async () => {
    if (!contact.organization_id) {
      toast({
        title: "Missing Organization",
        description: "Contact must be assigned to an organization.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
    let invitationId: string | undefined;
    let invitedRole: string = contact.intended_system_role || "standard";
      const newExpiry = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();

      // Always look up the invitation by email + organisation (one per pair),
      // including expired/processed ones that the status hook may not report.
      const { data: existing } = await supabase
        .from("user_invitations")
        .select("id, invited_role, status")
        .eq("email", contact.email)
        .eq("organization_id", contact.organization_id)
        .maybeSingle();

      if (existing) {
        if ((existing as any).status === "accepted") {
          toast({
            title: "Already accepted",
            description: "This contact has already accepted their invitation.",
          });
          return;
        }
        const { error: updateError } = await supabase
          .from("user_invitations")
          .update({ expires_at: newExpiry, status: "pending" } as any)
          .eq("id", existing.id);
        if (updateError) {
          throw new Error(`Could not refresh the invitation: ${updateError.message}`);
        }
        invitationId = existing.id;
        invitedRole = (existing as any).invited_role || invitedRole;
      } else {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("User not authenticated");
        const { data: newInvitation, error: insertError } = await supabase
          .from("user_invitations")
          .insert({
            email: contact.email,
            organization_id: contact.organization_id,
            invited_role: invitedRole as any,
            invited_by: user.id,
            device_ids: [],
            expires_at: newExpiry,
          } as any)
          .select()
          .single();
        if (insertError || !newInvitation) {
          throw new Error(
            `Could not create the invitation record${insertError?.message ? `: ${insertError.message}` : ""}`
          );
        }
        invitationId = newInvitation.id;
        invitedRole = newInvitation.invited_role || invitedRole;
      }
      queryClient.invalidateQueries({ queryKey: ["contact-invitation-status", contact.email] });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });

      let link: string;
      try {
        link = await generateInvitationLink({
          invitationId: invitationId!,
          contactId: contact.id,
          organizationId: contact.organization_id,
          invitedRole,
          email: contact.email,
        });
      } catch (linkError) {
        throw new Error(
          `Could not build the invitation link${linkError instanceof Error ? `: ${linkError.message}` : ""}`
        );
      }

      const didCopy = await copyToClipboard(link);

      if (didCopy) {
        setCopied(true);
        toast({
          title: "Link Copied",
          description: "Invitation link copied to clipboard. You can now paste it into an email.",
        });
        setTimeout(() => setCopied(false), 2000);
      } else {
        // Browser blocked copying — show the link so it can be copied by hand.
        setManualLink(link);
        toast({
          title: "Copy Blocked By Browser",
          description: "Your browser blocked automatic copying, so the link is shown for you to copy.",
        });
      }
    } catch (err) {
      console.error("Copy invitation link error:", err);
      toast({
        title: "Copy Failed",
        description: err instanceof Error ? err.message : "Could not copy invitation link",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const tooltipText = "Copy invitation link to share manually";

  return (
    <TooltipProvider>
      <Dialog open={!!manualLink} onOpenChange={(open) => !open && setManualLink(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Invitation Link</DialogTitle>
            <DialogDescription>
              Your browser blocked automatic copying. Select the link below and copy it, then send it
              to {contact.email}.
            </DialogDescription>
          </DialogHeader>
          <Input
            readOnly
            value={manualLink ?? ""}
            onFocus={(e) => e.currentTarget.select()}
            className="text-xs"
          />
        </DialogContent>
      </Dialog>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCopy}
              disabled={loading}
              className="h-9 px-2.5 bg-white font-semibold text-black"
              style={{ borderColor: "#FB4F2B" }}
              aria-label="Copy invitation link"
            >
              {loading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : copied ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <LinkIcon className="h-3.5 w-3.5" />
              )}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{tooltipText}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
